import React, { useMemo } from 'react';
import { Box, Card, CardHeader, CardContent, Typography, CircularProgress } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { usePerformanceReportingContext } from '../PerformanceReportingContext';
import useDimensions from '../_data_sources/useDimensions';
import { Dimension, DimensionDataPoint } from '../_types';
import { neutral, primary } from '_lex/_foundations/phxColors';

interface LegendItemProps {
  color: string;
  label: string;
  value: string;
}

function LegendItem({ color, label, value }: LegendItemProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: 1,
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 16,
          height: 16,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            background: color,
            borderRadius: '50%',
            width: '50%',
            height: '50%',
            position: 'absolute',
            right: '25%',
            left: '25%',
            bottom: '25%',
            top: '25%',
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
        }}
      >
        <Typography
          sx={{
            color: neutral[500],
            fontSize: 12,
            fontFamily: 'Inter',
            fontWeight: 400,
            lineHeight: '16.8px',
            textAlign: 'left',
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            color: neutral[700],
            fontSize: 16,
            fontFamily: 'Inter',
            fontWeight: 500,
            lineHeight: '24px',
            textAlign: 'left',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

function DimensionChart({ dimension }: { dimension: Dimension }) {
  const total = useMemo(() => {
    return dimension.data.reduce((sum, item) => sum + item.value, 0);
  }, [dimension.data]);

  const chartData = useMemo(() => {
    return dimension.data.map((item, index) => ({
      id: index,
      value: item.value,
      label: item.name,
    }));
  }, [dimension.data]);

  // Generate colors for the chart
  const chartColors = useMemo(() => {
    const colors = [
      primary[800],
      primary[700],
      primary[600],
      primary[500],
      primary[400],
      primary[300],
      primary[200],
      neutral[700],
      neutral[600],
      neutral[500],
      neutral[400],
      neutral[300],
    ];
    return chartData.map((_, index) => colors[index % colors.length]);
  }, [chartData.length]);

  const legendData = useMemo(() => {
    return dimension.data
      .map((item, index) => {
        const percent = total > 0 ? Math.round((item.value / total) * 100 * 10) / 10 : 0;
        // Format to 1 decimal place, but remove .0 if it's a whole number
        const percentStr = percent % 1 === 0 ? percent.toString() : percent.toFixed(1);
        return {
          label: item.name,
          value: item.value,
          percent: percentStr,
          color: chartColors[index],
        };
      })
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [dimension.data, total, chartColors]);

  if (total === 0) {
    return (
      <Card sx={{ borderRadius: 3, border: `1px solid ${neutral[300]}` }}>
        <CardHeader title={dimension.name} />
        <CardContent>
          <Box sx={{ width: '100%', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography
              sx={{
                color: neutral[500],
                fontSize: 14,
              }}
            >
              No data available
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 3, border: `1px solid ${neutral[300]}` }}>
      <CardHeader title={dimension.name} />
      <CardContent>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            gap: 6,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PieChart
              colors={chartColors}
              series={[
                {
                  data: chartData,
                  innerRadius: 60,
                  outerRadius: 100,
                  paddingAngle: 0,
                  cornerRadius: 0,
                  startAngle: 0,
                  endAngle: 360,
                  valueFormatter: (item) => {
                    const dataPoint = dimension.data[item.id as number];
                    return dataPoint.description.join('\n');
                  },
                },
              ]}
              margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
              height={200}
              width={200}
              slotProps={{
                legend: { hidden: true },
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              alignItems: 'flex-start',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {legendData.map((item) => (
              <LegendItem
                key={item.label}
                color={item.color}
                label={item.label}
                value={`${item.percent}%`}
              />
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export function DimensionsCharts() {
  const { orgId, childOrgId, queryParams } = usePerformanceReportingContext();
  const { data: dimensions, isLoading } = useDimensions({
    orgId,
    childOrgId,
    assignedModule: queryParams.assignedModule,
    startDate: queryParams.startDate,
    endDate: queryParams.endDate,
  });

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!dimensions || dimensions.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 4,
      }}
    >
      {dimensions.map((dimension, index) => (
        <DimensionChart
          key={`dimension-${dimension.name}-${index}`}
          dimension={dimension}
        />
      ))}
    </Box>
  );
}

export default DimensionsCharts;
